import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  markNotificationsRead,
  type NotificationItem,
} from '@suangua/shared';
import { fromNow } from '../components/ui/atoms';

/**
 * 消息通知页（0005）：
 * 顶栏铃铛入口。首次加载后自动全部标记已读（顶栏角标随轮询/路由切换清零）。
 * 类型图标：点赞 / 回复 / 管理处置 / 举报结果；关联帖子可点击跳转详情（已删除则不可点）。
 */

const TYPE_META: Record<NotificationItem['type'], { icon: string; cls: string; label: string }> = {
  like: { icon: 'fa-solid fa-heart', cls: 'text-cinnabar-500', label: '点赞' },
  comment_like: { icon: 'fa-solid fa-heart', cls: 'text-cinnabar-500', label: '点赞' },
  reply: { icon: 'fa-regular fa-comment-dots', cls: 'text-gold-400', label: '回复' },
  reply_to: { icon: 'fa-regular fa-comment', cls: 'text-gold-400', label: '回复' },
  moderation: { icon: 'fa-solid fa-shield-halved', cls: 'text-jade-400', label: '系统处置' },
  report: { icon: 'fa-regular fa-circle-check', cls: 'text-jade-400', label: '举报反馈' },
};

const PAGE_SIZE = 20;

export function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchNotifications(p, PAGE_SIZE);
      setItems((prev) => (p === 1 ? res.items : [...prev, ...res.items]));
      setTotal(res.total);
      setPage(res.page);
      // 首屏加载后全部标记已读（角标由布局层轮询/路由切换刷新）
      if (res.unread > 0) await markNotificationsRead({ all: true }).catch(() => undefined);
    } catch {
      setError('消息加载失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(1);
  }, [load]);

  return (
    <section className="mx-auto max-w-[760px]">
      <div className="page-head">
        <h1 className="page-title font-zh">消息通知</h1>
        <span className="page-sub">点赞 · 回复 · 系统处置</span>
      </div>

      {error && <p className="mb-3 rounded-lg border border-line bg-fill px-4 py-2.5 text-[12px] text-cinnabar-500">{error}</p>}

      <div className="space-y-2">
        {items.length === 0 && !loading ? (
          <div className="empty-card">
            <i className="fa-regular fa-bell-slash" />
            <p>暂无消息</p>
          </div>
        ) : (
          items.map((n) => {
            const meta = TYPE_META[n.type] ?? TYPE_META.moderation;
            const clickable = n.post_id !== null;
            return (
              <div
                key={n.id}
                className={`ink-card flex items-start gap-3 p-4${clickable ? ' cursor-pointer transition-transform active:scale-[.995]' : ''}`}
                onClick={() => clickable && navigate(`/board/${n.post_id}`)}
              >
                <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-fill ${meta.cls}`}>
                  <i className={`${meta.icon} text-[13px]`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-relaxed text-t1">
                    {!n.is_read && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-cinnabar-500 align-middle" />}
                    {n.content}
                  </p>
                  <p className="mt-1 text-[11px] text-t3">
                    <span className="mr-2">{meta.label}</span>
                    {fromNow(n.created_at)}
                    {clickable && <span className="ml-2 text-gold-400">查看相关帖子 →</span>}
                  </p>
                </div>
              </div>
            );
          })
        )}
        {loading && <p className="py-6 text-center text-[12px] text-t3">加载中…</p>}
        {!loading && items.length < total && (
          <button className="ghost-btn" onClick={() => void load(page + 1)}>
            加载更多（{items.length} / {total}）
          </button>
        )}
      </div>
    </section>
  );
}
